/** As folhas do Planejado: recorrência e parcelamento, criar e EDITAR.
 *
 * Escritas em 12/09 depois que o Paulo clicou nos dois botões e o app o jogou de volta na
 * tela Hoje: os links existiam, as rotas não. Os hooks (`useCreateRecurrence`,
 * `useCreatePlan`) e o preview da API já estavam prontos desde a F1 — faltava a tela.
 *
 * Em 14/09 ganharam o modo edição (C3 da auditoria). O lápis do Planejado abria uma folha
 * com "Pausar · Fechar · Excluir": prometia edição e não entregava, e "o aluguel subiu"
 * exigia apagar e recriar — perdendo o histórico da regra. **Um formulário só** serve aos
 * dois modos de propósito: dois formulários para a mesma coisa divergem com o tempo, e o
 * que diverge num app de dinheiro é o número.
 *
 * As duas mostram o que vão criar (ou passar a gerar) ANTES de salvar, usando o preview do
 * servidor: as próximas ocorrências de uma regra, as parcelas de um plano. Regra de data
 * não se adivinha no olho, nem na criação nem na edição.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useToast } from "../components/Toast";
import { ErrorBanner, Field, FieldGroup, Key, Label, Skeleton, Switch } from "../components/ui";
import { dayMonth, hojeLocal, money, parseMoney, weekday } from "../lib/format";
import {
  useAccounts,
  useCategories,
  useCreatePlan,
  useCreateRecurrence,
  usePlans,
  useRecurrences,
  useUpdatePlan,
  useUpdateRecurrence,
} from "../lib/queries";
import * as api from "../lib/api";
import type {
  Category,
  DueRule,
  Frequency,
  InstallmentPlan,
  Occurrence,
  Recurrence,
  TxKind,
} from "../lib/types";

const hojeISO = () => hojeLocal();
const num = (s: string) => parseMoney(s);
/** Dinheiro do servidor no campo de digitar: sempre com as duas casas, em pt-BR. */
const paraCampo = (v: number) => v.toFixed(2).replace(".", ",");

/** Casca comum: a folha, o título e os botões. Evita repetir o diálogo duas vezes. */
function Folha({
  titulo,
  children,
  podeSalvar,
  salvando,
  rotulo,
  onSalvar,
  erro,
}: {
  titulo: string;
  children: React.ReactNode;
  podeSalvar: boolean;
  salvando: boolean;
  rotulo: string;
  onSalvar: () => void;
  erro: string | null;
}) {
  const nav = useNavigate();
  const fechar = () => nav("/planejado");
  return (
    <div className="sheet on" role="dialog" aria-modal="true" aria-labelledby="folha-titulo">
      <div className="panel">
        <div className="handle" />
        <h2 id="folha-titulo">
          <span>{titulo}</span>
          <Key icon="x" title="Fechar" onClick={fechar} />
        </h2>
        {children}
        {erro && (
          <div className="banner" role="alert">
            {erro}
          </div>
        )}
        <div className="acts">
          <Key onClick={fechar}>Cancelar</Key>
          <Key primary disabled={!podeSalvar || salvando} onClick={onSalvar}>
            {salvando ? "Salvando…" : rotulo}
          </Key>
        </div>
      </div>
    </div>
  );
}

/** A mesma folha enquanto o registro a editar ainda não chegou — ou quando ele não existe
 *  mais. Abrir um formulário vazio "de edição" faria a pessoa digitar por cima do nada. */
function FolhaAviso({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  const nav = useNavigate();
  const fechar = () => nav("/planejado");
  return (
    <div className="sheet on" role="dialog" aria-modal="true" aria-labelledby="folha-titulo">
      <div className="panel">
        <div className="handle" />
        <h2 id="folha-titulo">
          <span>{titulo}</span>
          <Key icon="x" title="Fechar" onClick={fechar} />
        </h2>
        {children}
        <div className="acts">
          <Key onClick={fechar}>Fechar</Key>
        </div>
      </div>
    </div>
  );
}

/** As próximas datas que a regra (ou o plano) vai gerar, vindas do servidor. */
function Previsao({
  itens,
  titulo = "As próximas",
}: {
  itens: { chave: string; data: string; valor?: number }[];
  titulo?: string;
}) {
  if (itens.length === 0) return null;
  return (
    <div className="face impact" style={{ display: "block" }}>
      <Label>{titulo}</Label>
      <div className="chips" style={{ marginTop: 6 }}>
        {itens.map((o) => (
          <span className="chip" key={o.chave} aria-pressed={false}>
            {dayMonth(o.data)} {weekday(o.data)}
            {o.valor !== undefined ? ` · ${money(o.valor)}` : ""}
          </span>
        ))}
      </div>
    </div>
  );
}

// --- recorrência ------------------------------------------------------------

type Tipo = "in" | "out" | "dy" | "sv";

/** Cada tipo diz que família de categoria oferecer. Mesmo defeito que a folha Lançar
 *  tinha: numa SAÍDA, a lista trazia "Salário" e "Freela" e não trazia Energia, Internet
 *  nem Água — corrigi lá em 12/09 e deixei este aqui. */
const TIPOS: { id: Tipo; label: string; kind: TxKind; bucket: string; categorias: string[] }[] = [
  { id: "in", label: "Entrada", kind: "income", bucket: "income", categorias: ["income"] },
  { id: "out", label: "Saída", kind: "expense", bucket: "fixed", categorias: ["fixed", "variable"] },
  { id: "dy", label: "Diário", kind: "expense", bucket: "daily", categorias: ["daily"] },
  { id: "sv", label: "Economia", kind: "transfer", bucket: "savings", categorias: ["savings"] },
];

const FREQS: { id: Frequency; label: string }[] = [
  { id: "monthly", label: "Todo mês" },
  { id: "weekly", label: "Toda semana" },
  { id: "daily", label: "Todo dia" },
  { id: "yearly", label: "Todo ano" },
];

const REGRAS: { id: DueRule; label: string }[] = [
  { id: "fixed_day", label: "Dia fixo" },
  { id: "last_day", label: "Último dia do mês" },
  { id: "nth_business_day", label: "N-ésimo dia útil" },
  { id: "last_business_day", label: "Último dia útil" },
];

const SEMANA = ["seg", "ter", "qua", "qui", "sex", "sáb", "dom"];

/** O tipo de uma recorrência já salva. O banco guarda `tx_kind`, não o "Diário" da tela:
 *  quem separa Diário de Saída é o bucket da categoria, como no Lançar. */
function tipoDaRecorrencia(r: Recurrence, cats: Category[]): Tipo {
  if (r.tx_kind === "income") return "in";
  if (r.tx_kind === "transfer") return "sv";
  return cats.find((c) => c.id === r.category_id)?.bucket === "daily" ? "dy" : "out";
}

/** Rota `/planejado/nova-recorrencia` e `/planejado/recorrencia/:id`: o mesmo formulário. */
export function NovaRecorrencia() {
  const { id } = useParams();
  const recs = useRecurrences();
  const cats = useCategories();

  if (!id) return <FormRecorrencia />;

  const titulo = "Editar recorrência";
  // As categorias entram na conta porque é o bucket delas que diz se a regra é Diário ou
  // Saída — abrir o formulário antes de saber disso o abriria na aba errada.
  if (recs.isPending || cats.isPending)
    return (
      <FolhaAviso titulo={titulo}>
        <Skeleton lines={5} />
      </FolhaAviso>
    );
  if (recs.error)
    return (
      <FolhaAviso titulo={titulo}>
        <ErrorBanner message={(recs.error as Error).message} onRetry={() => recs.refetch()} />
      </FolhaAviso>
    );

  const original = (recs.data ?? []).find((r) => r.id === id);
  if (!original)
    return (
      <FolhaAviso titulo={titulo}>
        <div className="rule">
          Esta recorrência não existe mais. Ela pode ter sido excluída em outro aparelho.
        </div>
      </FolhaAviso>
    );

  return (
    <FormRecorrencia
      key={original.id}
      original={original}
      tipoInicial={tipoDaRecorrencia(original, cats.data ?? [])}
    />
  );
}

function FormRecorrencia({
  original,
  tipoInicial,
}: {
  original?: Recurrence;
  tipoInicial?: Tipo;
}) {
  const nav = useNavigate();
  const aviso = useToast();
  const criar = useCreateRecurrence();
  const salvar = useUpdateRecurrence();
  const { data: contas } = useAccounts();
  const { data: cats } = useCategories();
  const editando = !!original;

  const [tipo, setTipo] = useState<Tipo>(tipoInicial ?? "out");
  const [nome, setNome] = useState(original?.name ?? "");
  const [valor, setValor] = useState(original ? paraCampo(original.amount) : "");
  const [contaId, setContaId] = useState(original?.account_id ?? "");
  const [destinoId, setDestinoId] = useState(original?.counter_account_id ?? "");
  const [catId, setCatId] = useState(original?.category_id ?? "");
  const [freq, setFreq] = useState<Frequency>(original?.frequency ?? "monthly");
  const [dia, setDia] = useState(String(original?.day ?? new Date().getDate()));
  const [regra, setRegra] = useState<DueRule>(original?.due_rule ?? "fixed_day");
  const [nth, setNth] = useState(String(original?.nth ?? 1));
  const [diaSemana, setDiaSemana] = useState(original?.weekday ?? 0);
  const [comeca, setComeca] = useState(original?.starts_on ?? hojeISO());
  const [diaUtil, setDiaUtil] = useState(original?.adjust_business_day ?? false);
  const [variavel, setVariavel] = useState(original?.is_variable ?? false);
  const [temFim, setTemFim] = useState(original?.max_occurrences != null);
  const [vezes, setVezes] = useState(String(original?.max_occurrences ?? 12));
  const [previsao, setPrevisao] = useState<Occurrence[]>([]);
  const [erro, setErro] = useState<string | null>(null);

  const t = TIPOS.find((x) => x.id === tipo)!;
  const possiveis = useMemo(() => {
    const all = contas ?? [];
    // Receita e economia não entram em cartão nem em crediário (o motor recusa).
    if (tipo === "in" || tipo === "sv") return all.filter((a) => a.in_cashflow);
    return all.filter((a) => a.in_cashflow || a.kind === "credit_card" || a.kind === "loan");
  }, [contas, tipo]);
  const reservas = useMemo(
    () => (contas ?? []).filter((a) => a.kind === "savings" || a.kind === "investment"),
    [contas],
  );
  // Sem `?? possiveis[0]` quando há conta escolhida: se a conta da regra sumiu da lista
  // (arquivada, ou o tipo mudou e ela não serve mais), cair no primeiro item MOVERIA a
  // recorrência de conta em silêncio. Melhor não salvar do que salvar em outro lugar.
  const conta = contaId ? possiveis.find((a) => a.id === contaId) : possiveis[0];
  const contaSumiu = !!contaId && !conta;
  const categorias = (cats ?? []).filter((c) => t.categorias.includes(c.bucket));
  const amount = num(valor);
  const valido = nome.trim().length > 0 && amount > 0 && !!conta;

  const corpo = useMemo(
    () => ({
      name: nome.trim() || "—",
      account_id: conta?.id ?? "",
      tx_kind: t.kind,
      amount,
      kind: (tipo === "in" ? "income" : tipo === "sv" ? "savings" : "bill") as
        | "income"
        | "savings"
        | "bill",
      frequency: freq,
      interval: 1,
      due_rule: freq === "monthly" || freq === "yearly" ? regra : "fixed_day",
      day: regra === "fixed_day" ? Math.min(31, Math.max(1, Number(dia) || 1)) : null,
      weekday: freq === "weekly" ? diaSemana : null,
      nth: regra === "nth_business_day" ? Math.max(1, Number(nth) || 1) : null,
      adjust_business_day: diaUtil,
      starts_on: comeca,
      max_occurrences: temFim ? Math.max(1, Number(vezes) || 1) : null,
      is_variable: variavel,
      counter_account_id: tipo === "sv" ? destinoId || reservas[0]?.id || null : null,
      category_id: catId || null,
    }),
    [nome, conta, t, amount, tipo, freq, regra, dia, diaSemana, nth, diaUtil, comeca, temFim, vezes, variavel, destinoId, reservas, catId],
  );

  // O preview vem do servidor: quem sabe feriado de MT e dia útil é o motor, não a tela.
  // Vale igual na edição — mudar o dia 5 para "2º dia útil" precisa mostrar que datas passam
  // a sair, antes de a regra valer para os próximos meses.
  useEffect(() => {
    if (!conta) return;
    let vivo = true;
    api
      .previewRecurrence(corpo, 6)
      .then((o) => vivo && setPrevisao(o))
      .catch(() => vivo && setPrevisao([]));
    return () => {
      vivo = false;
    };
  }, [corpo, conta]);

  const enviar = async () => {
    if (!valido) return;
    setErro(null);
    try {
      if (original) {
        await salvar.mutateAsync({ id: original.id, ...corpo });
        aviso.ok(`${corpo.name}: a projeção refez as próximas`);
      } else {
        await criar.mutateAsync(corpo);
        aviso.ok(`${corpo.name} entra no fluxo a partir de agora`);
      }
      nav("/planejado");
    } catch (e) {
      setErro((e as Error).message);
    }
  };

  return (
    <Folha
      titulo={editando ? "Editar recorrência" : "Nova recorrência"}
      podeSalvar={valido}
      salvando={criar.isPending || salvar.isPending}
      rotulo={editando ? "Salvar" : "Criar"}
      onSalvar={enviar}
      erro={erro}
    >
      {editando && (
        <div className="rule">
          O que mudar aqui vale para as próximas ocorrências. O que já foi pago ou confirmado
          fica como está no histórico.
        </div>
      )}

      <div className="kinds" role="group" aria-label="Tipo">
        {TIPOS.map((x) => (
          <button
            key={x.id}
            type="button"
            className={`key ${x.id}`}
            aria-pressed={tipo === x.id}
            onClick={() => {
              setTipo(x.id);
              setCatId("");
              setContaId("");
            }}
          >
            {x.label}
          </button>
        ))}
      </div>

      <Field label="Nome">
        <input
          placeholder="Ex.: Aluguel, Salário, Netflix"
          value={nome}
          autoFocus
          onChange={(e) => setNome(e.target.value)}
        />
      </Field>

      <Field label="Valor" className="amount">
        <input inputMode="decimal" placeholder="0,00" value={valor} onChange={(e) => setValor(e.target.value)} />
      </Field>
      <Switch checked={variavel} onChange={setVariavel}>
        O valor muda todo mês (a conta de luz)
      </Switch>
      {variavel && (
        <div className="rule">
          Este valor vira estimativa; quando a conta chegar, você confirma o valor certo.
        </div>
      )}

      <FieldGroup label={tipo === "in" ? "Cai em" : "Sai de"}>
        <div className="chips">
          {possiveis.map((a) => (
            <button
              key={a.id}
              type="button"
              className="chip"
              aria-pressed={conta?.id === a.id}
              onClick={() => setContaId(a.id)}
            >
              <i style={{ background: a.color ?? "var(--muted)" }} />
              {a.name}
              {a.kind === "credit_card" ? " · cartão" : a.kind === "loan" ? " · crediário" : ""}
            </button>
          ))}
        </div>
      </FieldGroup>
      {contaSumiu && (
        <div className="banner" role="alert">
          A conta desta recorrência não está mais nesta lista — ela pode ter sido arquivada, ou
          não servir para o tipo que você escolheu. Escolha uma conta acima para poder salvar.
        </div>
      )}

      {tipo === "sv" && reservas.length > 0 && (
        <FieldGroup label="Vai para">
          <div className="chips">
            {reservas.map((a) => (
              <button
                key={a.id}
                type="button"
                className="chip"
                aria-pressed={(destinoId || reservas[0].id) === a.id}
                onClick={() => setDestinoId(a.id)}
              >
                <i style={{ background: a.color ?? "var(--muted)" }} />
                {a.name}
              </button>
            ))}
          </div>
        </FieldGroup>
      )}

      <FieldGroup label="Com que frequência">
        <div className="chips">
          {FREQS.map((f) => (
            <button
              key={f.id}
              type="button"
              className="chip"
              aria-pressed={freq === f.id}
              onClick={() => setFreq(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </FieldGroup>

      {freq === "weekly" && (
        <FieldGroup label="Em que dia da semana">
          <div className="chips">
            {SEMANA.map((d, i) => (
              <button
                key={d}
                type="button"
                className="chip"
                aria-pressed={diaSemana === i}
                onClick={() => setDiaSemana(i)}
              >
                {d}
              </button>
            ))}
          </div>
        </FieldGroup>
      )}

      {(freq === "monthly" || freq === "yearly") && (
        <>
          <FieldGroup label="Que dia">
            <div className="chips">
              {REGRAS.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className="chip"
                  aria-pressed={regra === r.id}
                  onClick={() => setRegra(r.id)}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </FieldGroup>
          {regra === "fixed_day" && (
            <Field label="Dia do mês">
              <input inputMode="numeric" value={dia} onChange={(e) => setDia(e.target.value)} />
            </Field>
          )}
          {regra === "nth_business_day" && (
            <Field label="Qual dia útil">
              <input inputMode="numeric" value={nth} onChange={(e) => setNth(e.target.value)} />
            </Field>
          )}
        </>
      )}

      {regra === "fixed_day" && freq !== "daily" && (
        <Switch checked={diaUtil} onChange={setDiaUtil}>
          Se cair em fim de semana ou feriado, adiar para o dia útil seguinte
        </Switch>
      )}

      <Field label="Começa em">
        <input type="date" value={comeca} onChange={(e) => setComeca(e.target.value)} />
      </Field>

      <Switch checked={temFim} onChange={setTemFim}>
        Tem fim (número de vezes)
      </Switch>
      {temFim && (
        <Field label="Quantas vezes">
          <input inputMode="numeric" value={vezes} onChange={(e) => setVezes(e.target.value)} />
        </Field>
      )}

      {categorias.length > 0 && (
        <FieldGroup label="Categoria">
          <div className="chips">
            {categorias.slice(0, 12).map((c) => (
              <button
                key={c.id}
                type="button"
                className="chip"
                aria-pressed={catId === c.id}
                onClick={() => setCatId(c.id)}
              >
                {c.name}
              </button>
            ))}
          </div>
        </FieldGroup>
      )}

      <Previsao itens={previsao.map((o) => ({ chave: String(o.n), data: o.date }))} />
    </Folha>
  );
}

// --- parcelamento -----------------------------------------------------------

/** Rota `/planejado/novo-parcelamento` e `/planejado/parcelamento/:id`. */
export function NovoParcelamento() {
  const { id } = useParams();
  const plans = usePlans();

  if (!id) return <FormParcelamento />;

  const titulo = "Editar parcelamento";
  if (plans.isPending)
    return (
      <FolhaAviso titulo={titulo}>
        <Skeleton lines={5} />
      </FolhaAviso>
    );
  if (plans.error)
    return (
      <FolhaAviso titulo={titulo}>
        <ErrorBanner message={(plans.error as Error).message} onRetry={() => plans.refetch()} />
      </FolhaAviso>
    );

  const original = (plans.data ?? []).find((p) => p.id === id);
  if (!original)
    return (
      <FolhaAviso titulo={titulo}>
        <div className="rule">
          Este parcelamento não existe mais. Ele pode ter sido excluído em outro aparelho.
        </div>
      </FolhaAviso>
    );

  return <FormParcelamento key={original.id} original={original} />;
}

function FormParcelamento({ original }: { original?: InstallmentPlan }) {
  const nav = useNavigate();
  const aviso = useToast();
  const criar = useCreatePlan();
  const salvar = useUpdatePlan();
  const { data: contas } = useAccounts();
  const { data: cats } = useCategories();
  const editando = !!original;

  const [desc, setDesc] = useState(original?.description ?? "");
  const [porTotal, setPorTotal] = useState(true);
  const [valor, setValor] = useState(original ? paraCampo(original.total_amount) : "");
  const [vezes, setVezes] = useState(String(original?.installment_count ?? 12));
  const [contaId, setContaId] = useState(original?.account_id ?? "");
  const [primeira, setPrimeira] = useState(original?.first_due_on ?? hojeISO());
  const [pagas, setPagas] = useState(String(original?.already_paid ?? 0));
  const [catId, setCatId] = useState(original?.category_id ?? "");
  const [loja, setLoja] = useState(original?.payee ?? "");
  const [diaUtil, setDiaUtil] = useState(original?.adjust_business_day ?? false);
  const [parcelas, setParcelas] = useState<{ no: number; due_on: string; amount: number }[]>([]);
  const [erro, setErro] = useState<string | null>(null);

  // Só cartão e crediário: uma compra parcelada é dívida, e é a dívida que a API guarda
  // (ela recusa plano em conta gastável). Boleto parcelado da loja é crediário.
  const possiveis = (contas ?? []).filter((a) => a.kind === "credit_card" || a.kind === "loan");
  // Na edição a conta não muda: `PATCH /installment-plans/{id}` não aceita `account_id`, e
  // fingir que aceita deixaria a tela mostrando uma coisa e o banco guardando outra.
  const conta = editando
    ? possiveis.find((a) => a.id === original.account_id)
    : possiveis.find((a) => a.id === contaId) ?? possiveis[0];
  const contaIdEfetiva = editando ? original.account_id : conta?.id ?? "";
  const n = Math.max(1, Number(vezes) || 1);
  const informado = num(valor);
  // A API guarda o total. Quem digita o valor da parcela (o que a loja diz) multiplica aqui —
  // era a lacuna L5 do mapa da v2.
  const total = porTotal ? informado : informado * n;
  const jaPagas = Math.min(n, Math.max(0, Number(pagas) || 0));
  const valido = desc.trim().length > 0 && total > 0 && !!contaIdEfetiva;

  const corpo = useMemo(
    () => ({
      description: desc.trim() || "—",
      account_id: contaIdEfetiva,
      total_amount: total,
      installment_count: n,
      first_due_on: primeira,
      already_paid: jaPagas,
      adjust_business_day: diaUtil,
      category_id: catId || null,
      payee: loja.trim() || null,
    }),
    [desc, contaIdEfetiva, total, n, primeira, jaPagas, diaUtil, catId, loja],
  );

  useEffect(() => {
    if (!contaIdEfetiva || total <= 0) return;
    let vivo = true;
    api
      .previewPlan(corpo)
      .then((p) => vivo && setParcelas(p))
      .catch(() => vivo && setParcelas([]));
    return () => {
      vivo = false;
    };
  }, [corpo, contaIdEfetiva, total]);

  const enviar = async () => {
    if (!valido) return;
    setErro(null);
    try {
      if (original) {
        // Só o que o PATCH aceita. `account_id` e o ajuste de dia útil ficam de fora de
        // propósito: mandá-los seria ruído que o servidor descarta em silêncio.
        await salvar.mutateAsync({
          id: original.id,
          description: corpo.description,
          total_amount: corpo.total_amount,
          installment_count: corpo.installment_count,
          first_due_on: corpo.first_due_on,
          already_paid: corpo.already_paid,
          category_id: corpo.category_id,
          payee: corpo.payee,
        });
        aviso.ok(`${corpo.description}: parcelas refeitas`);
      } else {
        await criar.mutateAsync(corpo);
        aviso.ok(`${corpo.description}: ${n}× no fluxo`);
      }
      nav("/planejado");
    } catch (e) {
      setErro((e as Error).message);
    }
  };

  const faltam = parcelas.reduce((s, p) => s + p.amount, 0);

  return (
    <Folha
      titulo={editando ? "Editar parcelamento" : "Novo parcelamento"}
      podeSalvar={valido}
      salvando={criar.isPending || salvar.isPending}
      rotulo={editando ? "Salvar" : "Criar"}
      onSalvar={enviar}
      erro={erro}
    >
      {editando && (
        <div className="rule">
          Mudar o total ou o número de parcelas refaz as que ainda não venceram. As já pagas
          ficam no histórico.
        </div>
      )}

      <Field label="O que foi">
        <input
          placeholder="Ex.: Geladeira, Pneus, Pós-graduação"
          value={desc}
          autoFocus
          onChange={(e) => setDesc(e.target.value)}
        />
      </Field>

      <FieldGroup label="O valor que você tem é">
        <div className="chips">
          <button type="button" className="chip" aria-pressed={porTotal} onClick={() => setPorTotal(true)}>
            O total da compra
          </button>
          <button type="button" className="chip" aria-pressed={!porTotal} onClick={() => setPorTotal(false)}>
            O valor da parcela
          </button>
        </div>
      </FieldGroup>

      <div className="row2">
        <Field label={porTotal ? "Total" : "Valor da parcela"} className="amount">
          <input inputMode="decimal" placeholder="0,00" value={valor} onChange={(e) => setValor(e.target.value)} />
        </Field>
        <Field label="Em quantas">
          <input inputMode="numeric" value={vezes} onChange={(e) => setVezes(e.target.value)} />
        </Field>
      </div>
      {informado > 0 && (
        <div className="rule">
          {porTotal
            ? `${n}× de ${money(total / n)}`
            : `total de ${money(total)} em ${n}×`}
        </div>
      )}

      {!editando && possiveis.length === 0 && (
        <div className="banner" role="alert">
          Você ainda não tem cartão nem crediário. Uma compra parcelada é dívida e precisa de um
          dos dois — crie em Contas → Novo cartão (ou Novo crediário) e volte aqui.
        </div>
      )}

      {editando ? (
        <FieldGroup label="Onde cai">
          <div className="chips">
            <span className="chip">
              <i style={{ background: conta?.color ?? "var(--muted)" }} />
              {conta?.name ?? original.account_name ?? "conta do plano"}
              {original.account_kind === "credit_card"
                ? " · cartão"
                : original.account_kind === "loan"
                  ? " · crediário"
                  : ""}
            </span>
          </div>
          <div className="rule">
            A conta de um parcelamento não muda depois de criado: a dívida já está registrada
            nela. Para mudar de cartão, exclua este plano e crie outro.
          </div>
        </FieldGroup>
      ) : (
        <FieldGroup label="Onde cai">
          <div className="chips">
            {possiveis.map((a) => (
              <button
                key={a.id}
                type="button"
                className="chip"
                aria-pressed={conta?.id === a.id}
                onClick={() => setContaId(a.id)}
              >
                <i style={{ background: a.color ?? "var(--muted)" }} />
                {a.name}
                {a.kind === "credit_card" ? " · cartão" : a.kind === "loan" ? " · crediário" : ""}
              </button>
            ))}
          </div>
        </FieldGroup>
      )}

      <div className="row2">
        <Field label="Primeira parcela vence">
          <input type="date" value={primeira} onChange={(e) => setPrimeira(e.target.value)} />
        </Field>
        <Field label="Já pagas">
          <input inputMode="numeric" value={pagas} onChange={(e) => setPagas(e.target.value)} />
        </Field>
      </div>
      <div className="rule">
        Compra que começou antes: diga quantas já foram. O motor gera só as que faltam.
      </div>

      {editando ? (
        <div className="rule">
          Ajuste de fim de semana e feriado: {diaUtil ? "adia para o dia útil seguinte" : "não adia"}.
          Este ajuste não muda pela edição.
        </div>
      ) : (
        <Switch checked={diaUtil} onChange={setDiaUtil}>
          Se a parcela cair em fim de semana ou feriado, adiar para o dia útil seguinte
        </Switch>
      )}

      <Field label="Loja (opcional)">
        <input placeholder="Ex.: Magazine Luiza" value={loja} onChange={(e) => setLoja(e.target.value)} />
      </Field>

      {(cats ?? []).length > 0 && (
        <FieldGroup label="Categoria">
          <div className="chips">
            {(cats ?? [])
              .filter((c) => c.bucket === "fixed" || c.bucket === "variable")
              .slice(0, 12)
              .map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="chip"
                  aria-pressed={catId === c.id}
                  onClick={() => setCatId(c.id)}
                >
                  {c.name}
                </button>
              ))}
          </div>
        </FieldGroup>
      )}

      <Previsao
        titulo={editando ? "Como ficam as próximas" : "As próximas"}
        itens={parcelas.slice(0, 6).map((p) => ({ chave: String(p.no), data: p.due_on, valor: p.amount }))}
      />
      {parcelas.length > 0 && (
        <div className="rule">
          {parcelas.length} parcela{parcelas.length > 1 ? "s" : ""} a pagar, {money(faltam)} no total.
        </div>
      )}
    </Folha>
  );
}
